import { inject, injectable, LazyServiceIdentifier } from 'inversify';
import { firstValueFrom, timeout } from 'rxjs';

import { Logger } from '../models/logger';
import {
    DEFAULT_IMPERSONATION_RETURN,
    IMPERSONATION_FORBIDDEN_MESSAGE,
    ImpersonationEndReason,
    ImpersonationRecord,
    ServerImpersonationInfo,
    activeImpersonation,
    clearImpersonation,
    hasImpersonationRecord,
    impersonationExpiresAtMs,
    isImpersonationExpired,
    readImpersonation,
    setImpersonationHandlers,
    writeImpersonationEndedNotice,
} from '../utils/impersonation-storage';
import { isAccessTokenStale, isAnonymousToken } from '../utils/token-expiry.util';
import { ApiConfig } from './api-config';
import { AuthService } from './auth.service';
import { LightHttp } from './light-http.service';
import { LoginService } from './login.service';
import { UIContext } from './ui-context';

/** Navigation seam (jsdom cannot navigate; tests replace `assign`). */
export const impersonationNavigation = {
    assign: (url: string): void => {
        if (typeof window !== 'undefined') window.location.assign(url);
    },
};

/** Largest delay setTimeout honours (~24.8 days); a longer one fires at once. */
const MAX_TIMER_MS = 2 ** 31 - 1;
const END_REQUEST_TIMEOUT_MS = 5000;
const RESTORE_TIMEOUT_MS = 4000;
const FORBIDDEN_NOTICE_GAP_MS = 3000;

/**
 * Admin v2 "log in as user" (API-CONTRACT §4–§5), the user-facing half (it ships in `_app`: the banner, expiry,
 * Sign out and every 401 need it). Starting a session is admin-only code: `ImpersonationStartService`
 * (services/impersonation-start.service.ts), loaded with the admin pages only.
 *
 * end:   POST /admin/impersonation/end with the impersonation token (errors ignored) → clear `AUTH_IMP` → reload the
 * admin's user details → back to `returnTo`. Also on expiry (timer + every request), on a 401, on "Sign out".
 *
 * The admin's localStorage bearer, refresh cookie and other tabs are never touched (see utils/impersonation-storage.ts).
 */
@injectable()
export class ImpersonationService {
    private readonly logger = Logger.getLogger('ImpersonationService');
    private expiryTimer: ReturnType<typeof setTimeout> | null = null;
    private visibilityBound = false;
    private ending: Promise<void> | null = null;
    /** Set by `ImpersonationStartService` while a start is verifying its token: a 401 then is the start's own failure. */
    startInProgress = false;
    private lastForbiddenNoticeAt = 0;
    private serverInfo: ServerImpersonationInfo | null = null;

    constructor(
        @inject(LightHttp) private http: LightHttp,
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(AuthService) private authService: AuthService,
        @inject(UIContext) private uiContext: UIContext,
        // Deferred: login.service is already in a lazy cycle with the auth layer.
        @inject(new LazyServiceIdentifier(() => LoginService)) private loginService: LoginService,
    ) {
        setImpersonationHandlers({
            end: (reason) => { void this.end(reason); },
            forbidden: () => this.noticeForbidden(),
        });
    }

    /** A present, unexpired record in this tab. */
    isActive(): boolean {
        return activeImpersonation() !== null;
    }

    current(): ImpersonationRecord | null {
        return readImpersonation();
    }

    /** Boot of a tab that holds a record: end it when already expired, else arm the expiry timer. */
    resume(): void {
        const rec = readImpersonation();
        if (!rec) return;
        if (isImpersonationExpired(rec)) {
            void this.end('expired');
            return;
        }
        this.armExpiry(impersonationExpiresAtMs(rec));
    }

    /**
     * Contract §4: the server says this bearer is an impersonation token but the tab has no record (the token was
     * copied). Keep the banner's End working and end at the server's `expiresAt`.
     */
    resumeFromServer(info: ServerImpersonationInfo | null): void {
        this.serverInfo = info && info.sessionId ? info : null;
        if (!this.serverInfo || hasImpersonationRecord()) return;
        const at = this.serverInfo.expiresAt ? Date.parse(this.serverInfo.expiresAt) : NaN;
        if (Number.isFinite(at)) this.armExpiry(at);
    }

    /**
     * End the session (idempotent; every caller of one end shares it). `user`/`logout`/`session_cleared` tell the
     * server; `expired`/`ended`/`unauthorized` do not (the server already refuses the token).
     */
    end(reason: ImpersonationEndReason = 'user'): Promise<void> {
        if (this.startInProgress) return Promise.resolve(); // the start cleans up its own failure
        if (this.ending) return this.ending;
        this.ending = this.doEnd(reason).catch((e) => {
            this.logger.error({ message: 'Ending impersonation failed', error: e });
        });
        return this.ending;
    }

    private async doEnd(reason: ImpersonationEndReason): Promise<void> {
        this.clearExpiry();
        const tellServer = reason === 'user' || reason === 'logout' || reason === 'session_cleared';
        const rec = readImpersonation();
        if (rec) {
            if (tellServer && !isImpersonationExpired(rec)) {
                await this.endOnServer(rec.token, rec.sessionId, reason);
            }
            clearImpersonation();
            if (reason !== 'user' && reason !== 'logout') writeImpersonationEndedNotice(reason);
            await this.restoreAdminDetails();
            impersonationNavigation.assign(rec.returnTo || DEFAULT_IMPERSONATION_RETURN);
            return;
        }
        // No record: the bearer itself is an impersonation token (server-reported). It is not the admin's, so drop it.
        const info = this.serverInfo ?? this.loginService.userDetails?.impersonation ?? null;
        if (info?.sessionId) {
            const token = this.authService.getToken();
            if (tellServer && token) await this.endOnServer(token, info.sessionId, reason);
            this.serverInfo = null;
            this.loginService.clearLocalSession();
            impersonationNavigation.assign('/');
        }
    }

    /** Best effort: never throws, bounded, sent with THIS session's token (not whatever getToken() says by then). */
    async endOnServer(token: string, sessionId: string, reason: string): Promise<void> {
        try {
            const url = `${this.apiConfig.getHttpsEndpoint()}/admin/impersonation/end`;
            await firstValueFrom(this.http.post(url, { sessionId, reason }, {
                headers: { Authorization: `Bearer ${token}` },
                __impersonationEnd: true,
                timeout: END_REQUEST_TIMEOUT_MS,
            }));
        } catch (e) {
            this.logger.info({ message: 'impersonation/end failed (ignored)', error: e });
        }
    }

    /** Re-read the admin's account with the admin's bearer, when it is usable as is (no refresh from here). */
    async restoreAdminDetails(): Promise<void> {
        const token = this.authService.getToken();
        if (!token || isAnonymousToken(token) || isAccessTokenStale(token)) return; // the next page's boot refreshes
        try {
            await firstValueFrom(this.loginService.reloadUserDetails$().pipe(timeout(RESTORE_TIMEOUT_MS)));
        } catch (e) {
            this.logger.info({ message: 'Admin user-details reload after impersonation failed (ignored)', error: e });
        }
    }

    armExpiry(atMs: number): void {
        this.clearExpiry();
        const delay = Math.max(0, Math.min(atMs - Date.now(), MAX_TIMER_MS));
        this.expiryTimer = setTimeout(() => { void this.end('expired'); }, delay);
        // A suspended / backgrounded tab can miss its timer: check again whenever it becomes visible.
        if (!this.visibilityBound && typeof document !== 'undefined') {
            this.visibilityBound = true;
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState !== 'visible') return;
                const rec = readImpersonation();
                if (rec && isImpersonationExpired(rec)) void this.end('expired');
            });
        }
    }

    private clearExpiry(): void {
        if (this.expiryTimer) clearTimeout(this.expiryTimer);
        this.expiryTimer = null;
    }

    private noticeForbidden(): void {
        const now = Date.now();
        if (now - this.lastForbiddenNoticeAt < FORBIDDEN_NOTICE_GAP_MS) return;
        this.lastForbiddenNoticeAt = now;
        try {
            this.uiContext.warnToast('Not allowed', IMPERSONATION_FORBIDDEN_MESSAGE, false, false);
        } catch { /* a notice never breaks the request path */ }
    }
}

export { IMPERSONATION_FORBIDDEN_MESSAGE };
